<!--
  AssignVoucherModal — 클래스에 도서·플랜(판매 등급) 지정
  흐름: 교재 검색(기존 TextbookSearchModal) → 교재 상세 GET /textbook/textbooks/{barcode} 의 pricing_grade 로 등급 선택지
        → GET /voucher/org/{org}/price?barcode&grade (정가→기관가, allowed_grades 재확인) → 기한·상한·메모 → POST
  정본: docs/바우처_기관발행_강사화면.md §3.3·§4.3
-->
<template>
  <a-modal
    :open="true"
    title="도서·플랜 지정"
    :sub="`${classroom.name} 수강생에게 지정할 도서와 등급을 고릅니다`"
    size="lg"
    @close="$emit('close')"
  >
    <div class="p-form">
      <!-- 1. 교재 -->
      <div class="p-field">
        <label class="p-field__label">교재 <span class="p-field__req">*</span></label>
        <div v-if="textbook" class="p-tb">
          <div class="p-tb__cover">
            <img v-if="textbook.cover_image_url" :src="textbook.cover_image_url" :alt="textbook.title" />
          </div>
          <div class="p-tb__body">
            <div class="p-cell-title">{{ textbook.title }}</div>
            <div class="p-cell-sub p-mono">{{ textbook.barcode }}<template v-if="textbook.publisher"> · {{ textbook.publisher }}</template></div>
            <div v-if="detailLoading" class="p-note">판매 등급을 확인하는 중...</div>
          </div>
          <a-btn size="sm" kind="ghost" @click="showSearch = true">다른 교재</a-btn>
        </div>
        <a-btn v-else kind="primary" @click="showSearch = true">교재 검색</a-btn>
        <div v-if="alreadyAssigned" class="p-alert p-alert--warn p-mt-8">
          <div class="p-alert__body">이 클래스에 같은 도서의 진행 중 지정이 이미 있습니다. 다른 도서를 고르거나 기존 지정을 종료한 뒤 다시 지정하세요.</div>
        </div>
      </div>

      <!-- 2. 등급 -->
      <div v-if="textbook" class="p-field">
        <label class="p-field__label">등급(플랜) <span class="p-field__req">*</span></label>
        <div class="p-grade-row">
          <button
            v-for="g in grades"
            :key="g"
            type="button"
            class="p-grade"
            :class="{ 'is-active': grade === g }"
            @click="selectGrade(g)"
          >{{ gradeLabel(g) }}</button>
        </div>
        <div class="p-field__hint">이 교재가 판매하는 등급만 표시됩니다.</div>
      </div>

      <!-- 3. 예상액 -->
      <div v-if="textbook && grade" class="p-price">
        <div v-if="priceLoading" class="p-note">예상액을 계산하는 중...</div>
        <template v-else-if="price">
          <div class="p-price__main">
            <span class="p-price__list">정가 {{ fmtAmount(price.list_price, price.currency_code) }}</span>
            <span class="p-price__arrow">→</span>
            <span class="p-price__org">기관가 {{ fmtAmount(price.amount, price.currency_code) }}</span>
            <a-pill kind="yellow">할인 {{ price.discount_rate }}%</a-pill>
            <a-pill v-if="price.feature_days" kind="info">이용 {{ price.feature_days }}일</a-pill>
          </div>
          <div class="p-note">학생이 등록할 때 그 시점 기관가로 차감됩니다.</div>
        </template>
        <div v-else-if="priceError" class="p-alert p-alert--err"><div class="p-alert__body">{{ priceError }}</div></div>
      </div>

      <!-- 4. 조건 -->
      <template v-if="textbook">
        <div class="p-form__row">
          <div class="p-field">
            <label class="p-field__label">등록 기한 <span class="p-field__req">*</span></label>
            <input v-model="form.register_until" type="date" class="p-input" :min="today" />
            <div class="p-field__hint">기본값은 클래스 종료일입니다. KST 기준 오늘 이후여야 합니다.</div>
          </div>
          <div class="p-field">
            <label class="p-field__label">인원 상한</label>
            <input v-model="form.max_registrations" type="number" min="1" step="1" class="p-input" placeholder="비우면 제한 없음" />
            <div class="p-field__hint">등록할 수 있는 최대 인원(선택)</div>
          </div>
        </div>
        <div class="p-field">
          <label class="p-field__label">메모</label>
          <input v-model.trim="form.memo" type="text" class="p-input" maxlength="200" placeholder="내부 메모 (선택)" />
        </div>
      </template>

      <div v-if="submitError" class="p-alert p-alert--err"><div class="p-alert__body">{{ submitError }}</div></div>
    </div>

    <template #foot>
      <a-btn kind="ghost" @click="$emit('close')">취소</a-btn>
      <a-btn kind="primary" :disabled="!canSubmit || submitting" @click="submit">{{ submitting ? '지정 중...' : '지정하기' }}</a-btn>
    </template>

    <textbook-search-modal v-if="showSearch" @select="onTextbookSelect" @close="showSearch = false" />
  </a-modal>
</template>

<script>
import AModal from '@/components/Pineapple/AModal.vue';
import ABtn from '@/components/Pineapple/ABtn.vue';
import APill from '@/components/Pineapple/APill.vue';
import TextbookSearchModal from '@/components/QuestionBank/TextbookSearchModal.vue';
import { GRADES, gradeLabel, parsePricingGrades, fmtAmount, isPositiveAmount, todayKst, voucherError, voucherErrorText } from '../orgUtils';

export default {
  name: 'AssignVoucherModal',
  components: { AModal, ABtn, APill, TextbookSearchModal },
  props: {
    orgIdx: { type: [Number, String], required: true },
    classroom: { type: Object, required: true },   // { classroom_idx, name, end_date, ... }
    currency: { type: String, default: '' },
    existingBarcodes: { type: Array, default: () => [] }, // 진행 중 지정의 바코드(중복 사전 안내)
  },
  emits: ['close', 'created'],
  data() {
    return {
      textbook: null,
      showSearch: false,
      grades: [...GRADES],
      grade: '',
      detailLoading: false,
      price: null,
      priceLoading: false,
      priceError: '',
      form: {
        register_until: this.classroom.end_date ? String(this.classroom.end_date).slice(0, 10) : '',
        max_registrations: '',
        memo: '',
      },
      submitting: false,
      submitError: '',
      today: todayKst(),
    };
  },
  computed: {
    alreadyAssigned() {
      return !!this.textbook && this.existingBarcodes.includes(this.textbook.barcode);
    },
    canSubmit() {
      return !!(this.textbook && this.grade && this.price && !this.priceError && this.form.register_until && !this.alreadyAssigned);
    },
  },
  methods: {
    gradeLabel,
    fmtAmount,
    onTextbookSelect(tb) {
      this.textbook = tb;
      this.showSearch = false;
      this.price = null;
      this.priceError = '';
      this.grade = '';
      this.submitError = '';
      this.loadDetail();
    },
    /** 교재 상세의 pricing_grade 로 등급 선택지 구성 (토큰 불필요 API). 실패하면 세 등급 전부 → price 가 allowed_grades 로 걸러 준다 */
    async loadDetail() {
      this.detailLoading = true;
      try {
        const res = await this.$axios({ method: 'get', url: `/textbook/textbooks/${this.textbook.barcode}`, loadingBar: false });
        const d = res.data || {};
        const raw = d.pricing_grade != null ? d.pricing_grade
          : (d.textbook && d.textbook.pricing_grade != null ? d.textbook.pricing_grade : null);
        this.grades = parsePricingGrades(raw);
      } catch (e) {
        this.grades = [...GRADES];
      } finally {
        this.detailLoading = false;
      }
      if (this.grades.length) this.selectGrade(this.grades[0]);
    },
    async selectGrade(g) {
      this.grade = g;
      await this.loadPrice();
    },
    async loadPrice() {
      if (!this.textbook || !this.grade) return;
      this.price = null;
      this.priceError = '';
      this.priceLoading = true;
      const wanted = this.grade;
      try {
        const res = await this.$axios({
          method: 'get',
          url: `/voucher/org/${this.orgIdx}/price`,
          params: { barcode: this.textbook.barcode, grade: wanted },
          loadingBar: false,
        });
        if (this.grade !== wanted) return; // 사이에 다른 등급을 눌렀으면 버림
        this.price = res.data || null;
        if (this.price && Array.isArray(this.price.allowed_grades) && this.price.allowed_grades.length) {
          this.grades = parsePricingGrades(this.price.allowed_grades);
        }
      } catch (e) {
        if (this.grade !== wanted) return;
        const err = voucherError(e);
        if (err.code === 'CLASS_VOUCHER_GRADE_NOT_ALLOWED' && err.detail && Array.isArray(err.detail.allowed_grades)) {
          this.grades = parsePricingGrades(err.detail.allowed_grades);
          if (!this.grades.includes(this.grade)) this.grade = '';
        }
        this.priceError = voucherErrorText(e, '예상액을 가져오지 못했습니다.');
      } finally {
        if (this.grade === wanted || !this.grade) this.priceLoading = false;
      }
    },
    async submit() {
      if (!this.canSubmit) return;
      this.submitting = true;
      this.submitError = '';
      try {
        const data = { barcode: this.textbook.barcode, grade: this.grade, register_until: this.form.register_until };
        const max = parseInt(this.form.max_registrations, 10);
        if (!Number.isNaN(max) && max > 0) data.max_registrations = max;
        if (this.form.memo) data.memo = this.form.memo;
        const res = await this.$axios({
          method: 'post',
          url: `/voucher/org/${this.orgIdx}/classrooms/${this.classroom.classroom_idx}/vouchers`,
          data,
          loadingBar: true,
        });
        const r = res.data || {};
        const cur = r.currency_code || this.currency;
        this.$emit('created', r);
        if (isPositiveAmount(r.balance_shortfall)) {
          await this.$swal({
            title: '지정은 완료됐습니다',
            text: `등록 대기 예상액이 잔액을 ${fmtAmount(r.balance_shortfall, cur)} 초과할 수 있습니다. 학생 등록이 막히지 않게 바우처 충전을 요청하세요.`,
            icon: 'warning',
            showConfirmButton: true,
            confirmButtonText: '확인',
          });
        } else {
          this.$notifyVue({
            type: 'success',
            title: `"${r.title || this.textbook.title}" (${gradeLabel(r.grade || this.grade)}) 을 지정했습니다.`,
            text: r.available_count != null ? `대상 ${r.available_count}명 · 예상 합계 ${fmtAmount(r.estimated_total, cur)}` : '',
          });
        }
        this.$emit('close');
      } catch (e) {
        const err = voucherError(e);
        if (err.code === 'CLASS_VOUCHER_GRADE_NOT_ALLOWED' && err.detail && Array.isArray(err.detail.allowed_grades)) {
          this.grades = parsePricingGrades(err.detail.allowed_grades);
        }
        this.submitError = voucherErrorText(e, '도서를 지정하지 못했습니다.');
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../_org.scss';

.p-tb {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid $p-line;
  border-radius: 10px;
  background: $p-panel-alt;
  &__cover {
    width: 40px;
    height: 54px;
    border-radius: 4px;
    background: $p-line-soft;
    overflow: hidden;
    flex-shrink: 0;
    img { width: 100%; height: 100%; object-fit: cover; }
  }
  &__body { flex: 1; min-width: 0; }
}
.p-grade-row { display: flex; gap: 8px; flex-wrap: wrap; }
.p-grade {
  padding: 8px 16px;
  border: 1px solid $p-line;
  border-radius: 8px;
  background: $p-panel;
  font-family: $p-font-body;
  font-size: 12px;
  font-weight: 700;
  color: $p-ink-mid;
  cursor: pointer;
  &:hover { border-color: $p-line-hard; color: $p-ink; }
  &.is-active { background: $p-ink; color: #fff; border-color: $p-ink; }
}
.p-price {
  padding: 12px 14px;
  border: 1px dashed $p-line-hard;
  border-radius: 10px;
  background: $p-point-soft;
  margin-bottom: 14px;
  &__main { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; font-size: 13px; }
  &__list { color: $p-ink-mid; text-decoration: line-through; }
  &__arrow { color: $p-ink-soft; }
  &__org { font-weight: 800; color: $p-ink; font-family: $p-font-mono; }
}
</style>
